// Knoten-Spieler: drei Ansichten derselben Zeichnungen — Schritt für Schritt, Animation, Übersicht.
// Gebraucht in der App (/tipps/knoten/…) und auf den öffentlichen Knotenseiten. Der Camper steht draußen, Seil in
// der einen, Handy in der anderen Hand: große Ziele (≥ 48 px), Bedienung mit einem Daumen, großer Schrittsatz.
//
//   mountPlayer(el, { knot, texts, storageKey, reducedMotion, mode }) → { play(), pause(), destroy(), state }
//   texts: { step(i), stepOf(n, m), title, modeLabel, modeSteps, modeAnim, modeList, prev, next, pause, play,
//            halfSpeed, slider } — fertig aus dem Wörterbuch über knotTexts(t, slug).
//
// Genau eine rAF-Schleife je Spieler (Callback `knFrame`), nur solange die Animation läuft. Bei reduzierter
// Bewegung startet die Animation nicht von selbst; „Abspielen“ rückt dann von Schrittbild zu Schrittbild vor.
import './player.css';
import { html, render, unsafeHtml as unsafe } from '../../ui/core/h.js'; // renderSvg: selbst erzeugtes, escaptes Markup
import { on, reducedMotion as isReducedMotion } from '../../ui/core/dom.js';
import { drag, spring, haptic } from '../../ui/motion/physics.js';
import { play, fadeThrough } from '../../ui/motion/motion.js';
import { paintSlider } from '../../ui/components/slider.js';
import { frame, renderSvg, stepAt } from './rope.js';
import { createPlayerState, stepView, MODES } from './player-state.js';

const icon = (n) => html`<span class="vc-icon" aria-hidden="true">${n}</span>`;
const MODE_ICON = { steps: 'steps', anim: 'play_arrow', list: 'grid_view' };
const SWIPE_PX = 56; // so weit ziehen = blättern
const SWIPE_V = 500; // oder so schnell werfen (px/s)

/** Texte für den Spieler aus dem Wörterbuch (Gruppe `knot`). */
export function knotTexts(t, slug) {
  return {
    title: t('knot.' + slug + '.name'),
    step: (i) => t('knot.' + slug + '.step_' + (i + 1)),
    stepOf: (n, m) => t('knot.ui.step_of', { n, m }),
    modeLabel: t('knot.ui.mode_label'),
    modeSteps: t('knot.ui.mode_steps'),
    modeAnim: t('knot.ui.mode_anim'),
    modeList: t('knot.ui.mode_list'),
    prev: t('knot.ui.prev'),
    next: t('knot.ui.next'),
    pause: t('knot.ui.pause'),
    play: t('knot.ui.play'),
    halfSpeed: t('knot.ui.half_speed'),
    slider: t('knot.ui.slider'),
  };
}

function readMode(key) {
  try { const v = localStorage.getItem(key); return MODES.includes(v) ? v : null; } catch { return null; }
}
function saveMode(key, v) {
  try { localStorage.setItem(key, v); } catch { /* privater Modus, voller Speicher */ }
}

export function mountPlayer(el, { knot, texts, storageKey = 'vc-knot-mode', reducedMotion, mode } = {}) {
  const T = texts;
  const n = (knot.steps || [1]).length;
  const reduced = () => (typeof reducedMotion === 'function' ? !!reducedMotion() : reducedMotion ?? isReducedMotion());
  const state = createPlayerState({ steps: knot.steps, mode: readMode(storageKey) || mode || 'steps', u: 0 });
  const svgFor = (u, o = {}) => renderSvg(knot, frame(knot, u), { ...o, title: o.title ?? T.title });

  render(el, html`
    <div class="kn-player">
      <div class="vc-btn-group is-connected kn-modes" role="group" aria-label="${T.modeLabel}">
        ${MODES.map((m) => html`<button type="button" class="vc-btn is-tonal is-m" data-kn-mode="${m}" aria-pressed="false">${icon(MODE_ICON[m])}${T['mode' + m[0].toUpperCase() + m.slice(1)]}</button>`)}
      </div>
      <section class="kn-view" data-kn-view="steps" hidden>
        <figure class="kn-stage" data-kn-swipe><div class="kn-art" data-kn-art="steps"></div></figure>
        <p class="kn-counter t-label-l" data-kn-counter></p>
        <p class="kn-sentence t-title-l" data-kn-sentence aria-live="polite"></p>
        <div class="kn-nav">
          <button type="button" class="vc-btn is-tonal is-m" data-kn-act="prev">${icon('chevron_left')}${T.prev}</button>
          <button type="button" class="vc-btn is-m" data-kn-act="next">${T.next}${icon('chevron_right')}</button>
        </div>
      </section>
      <section class="kn-view" data-kn-view="anim" hidden>
        <figure class="kn-stage"><div class="kn-art" data-kn-art="anim"></div></figure>
        <p class="kn-counter t-label-l" data-kn-anim-counter></p>
        <p class="kn-sentence t-title-l" data-kn-anim-sentence></p>
        <input type="range" class="vc-slider kn-slider" min="0" max="1000" step="1" value="0" aria-label="${T.slider}" data-kn-slider>
        <div class="kn-controls">
          <button type="button" class="vc-icon-btn is-m is-filled" data-kn-act="toggle" aria-label="${T.play}">${icon('play_arrow')}</button>
          <button type="button" class="vc-btn is-tonal is-m" data-kn-act="half" aria-pressed="false">${icon('speed')}${T.halfSpeed}</button>
        </div>
      </section>
      <section class="kn-view" data-kn-view="list" hidden>
        <ol class="kn-list">
          ${(knot.steps || [1]).map((_, i) => { const v = stepView(knot, i); return html`
            <li><button type="button" class="kn-list-item vc-state" data-kn-goto="${i}">
              <span class="kn-list-art" aria-hidden="true">${unsafe(renderSvg(knot, frame(knot, v.u), { highlight: v.highlight, pale: v.pale, arrow: v.arrow, title: '' }))}</span>
              <span class="kn-list-text"><span class="t-label-l kn-list-n">${T.stepOf(i + 1, n)}</span><span class="t-body-l">${T.step(i)}</span></span>
            </button></li>`; })}
        </ol>
      </section>
    </div>`);

  const root = el.querySelector('.kn-player');
  const $ = (s) => root.querySelector(s);
  const views = Object.fromEntries(MODES.map((m) => [m, $(`[data-kn-view="${m}"]`)]));
  const artSteps = $('[data-kn-art="steps"]'), artAnim = $('[data-kn-art="anim"]');
  const slider = $('[data-kn-slider]'), toggleBtn = $('[data-kn-act="toggle"]'), halfBtn = $('[data-kn-act="half"]');
  const offs = [];
  let raf = null, last = null, animStep = -1, dead = false;

  // ---------- Zeichnen ----------
  function drawSteps() {
    const i = state.step, v = stepView(knot, i);
    render(artSteps, unsafe(svgFor(v.u, { highlight: v.highlight, pale: v.pale, arrow: v.arrow, title: T.title + ' – ' + T.stepOf(i + 1, n) })));
    $('[data-kn-counter]').textContent = T.stepOf(i + 1, n);
    $('[data-kn-sentence]').textContent = T.step(i);
    $('[data-kn-act="prev"]').disabled = !state.canPrev;
    $('[data-kn-act="next"]').disabled = !state.canNext;
  }
  function drawAnim() {
    render(artAnim, unsafe(svgFor(state.u)));
    const i = stepAt(knot, state.u);
    if (i !== animStep) {
      animStep = i;
      $('[data-kn-anim-counter]').textContent = T.stepOf(i + 1, n);
      $('[data-kn-anim-sentence]').textContent = T.step(i);
      slider.setAttribute('aria-valuetext', T.stepOf(i + 1, n));
    }
    slider.value = String(Math.round(state.u * 1000));
    paintSlider(slider);
  }
  function drawControls() {
    const p = state.playing;
    toggleBtn.setAttribute('aria-label', p ? T.pause : T.play);
    toggleBtn.querySelector('.vc-icon').textContent = p ? 'pause' : 'play_arrow';
    halfBtn.setAttribute('aria-pressed', String(state.speed === 0.5));
  }
  function drawList() {
    const cur = state.step;
    root.querySelectorAll('[data-kn-goto]').forEach((b) => {
      if (Number(b.dataset.knGoto) === cur) b.setAttribute('aria-current', 'step'); else b.removeAttribute('aria-current');
    });
  }
  function draw() {
    if (state.mode === 'steps') drawSteps();
    else if (state.mode === 'anim') { drawAnim(); drawControls(); }
    else drawList();
  }

  // ---------- Takt: genau eine Schleife ----------
  function knFrame(ts) {
    raf = null;
    if (dead) return;
    const dt = last == null ? 1000 / 60 : Math.min(100, Math.max(0, ts - last));
    last = ts;
    if (state.tick(dt, { reduced: reduced() })) drawAnim();
    if (state.playing && state.mode === 'anim') raf = requestAnimationFrame(knFrame);
    else { last = null; drawControls(); }
  }
  knFrame.knPlayer = true; // Kennung für Tests (Funktionsnamen kürzt der Build)
  function startLoop() {
    if (raf != null || dead || !state.playing || state.mode !== 'anim') return;
    last = null;
    raf = requestAnimationFrame(knFrame);
  }
  function stopLoop() {
    if (raf != null) cancelAnimationFrame(raf);
    raf = null; last = null;
  }

  // ---------- Ansicht ----------
  function setMode(m, { animate = true, autoplay = true } = {}) {
    if (!MODES.includes(m)) return;
    const changed = m !== state.mode || views[m].hidden;
    state.setMode(m);
    if (m !== 'anim') stopLoop();
    for (const [k, v] of Object.entries(views)) v.hidden = k !== m;
    root.dataset.knCurrent = m; // nicht data-mode: das nutzen Seiten selbst (Katalog: Farbmodus)
    root.querySelectorAll('[data-kn-mode]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.knMode === m)));
    animStep = -1;
    if (m === 'anim' && autoplay && !reduced()) state.play();
    draw();
    if (m === 'anim') startLoop();
    if (changed && animate) play(views[m], fadeThrough(), { spring: 'effects-default' });
  }

  // ---------- Bedienung ----------
  offs.push(on(root, 'click', '[data-kn-mode]', (e, b) => {
    if (b.dataset.knMode === state.mode) return;
    saveMode(storageKey, b.dataset.knMode);
    setMode(b.dataset.knMode);
  }));
  const step = (dir) => {
    const before = state.u;
    if (dir > 0) state.next(); else state.prev();
    if (state.u !== before) { haptic('tick'); drawSteps(); }
  };
  offs.push(on(root, 'click', '[data-kn-act]', (e, b) => {
    const a = b.dataset.knAct;
    if (a === 'prev') step(-1);
    else if (a === 'next') step(1);
    else if (a === 'toggle') { state.toggle(); drawAnim(); drawControls(); startLoop(); if (!state.playing) stopLoop(); }
    else if (a === 'half') { state.setSpeed(state.speed === 1 ? 0.5 : 1); drawControls(); }
  }));
  offs.push(on(root, 'click', '[data-kn-goto]', (e, b) => {
    state.setMode('steps');
    state.setU(state.steps[Number(b.dataset.knGoto)]);
    saveMode(storageKey, 'steps');
    setMode('steps');
    $('[data-kn-act="next"]').focus({ preventScroll: true });
  }));
  const onInput = () => {
    state.pause(); stopLoop();
    state.setU(Number(slider.value) / 1000);
    drawAnim(); drawControls();
  };
  slider.addEventListener('input', onInput);
  offs.push(() => slider.removeEventListener('input', onInput));

  // Wischen im Schritt-Modus: das Bild folgt dem Finger, weit genug oder schnell genug = blättern.
  const swipe = $('[data-kn-swipe]');
  const sw = spring(0, { onUpdate: (x) => { artSteps.style.translate = x + 'px 0'; } });
  offs.push(drag(swipe, {
    axis: 'x',
    value: () => sw.value,
    filter: () => state.mode === 'steps',
    bounds: () => [state.canNext ? -Infinity : 0, state.canPrev ? Infinity : 0],
    dim: () => swipe.clientWidth || 300,
    onMove: (x) => sw.jump(x),
    onRelease: ({ delta, velocity }) => {
      if ((delta < -SWIPE_PX || velocity < -SWIPE_V) && state.canNext) step(1);
      else if ((delta > SWIPE_PX || velocity > SWIPE_V) && state.canPrev) step(-1);
      sw.to(0, { velocity });
    },
  }));
  offs.push(() => sw.stop());

  // ---------- Bildschirm an lassen ----------
  let lock = null, pending = null; // pending: laufende Anfrage — zwei gleichzeitige Anfragen ergäben zwei Sperren, eine davon verwaist
  function acquire() {
    if (dead || typeof document === 'undefined' || document.visibilityState !== 'visible') return pending;
    if ((lock && !lock.released) || pending) return pending;
    if (!navigator.wakeLock) return null;
    pending = (async () => {
      try {
        const l = await navigator.wakeLock.request('screen');
        if (!l) return;
        if (dead) { l.release().catch(() => {}); return; }
        lock = l;
      } catch { /* nicht erlaubt (Akku sparen, kein Fokus) — dann eben nicht */ } finally { pending = null; }
    })();
    return pending;
  }
  const onVis = () => { if (document.visibilityState === 'visible') acquire(); };
  document.addEventListener('visibilitychange', onVis);
  offs.push(() => document.removeEventListener('visibilitychange', onVis));
  acquire();

  setMode(state.mode, { animate: false });

  return {
    state,
    /** Von außen abspielen/anhalten (z. B. wenn die Anleitung zugeklappt wird); wirkt nur in der Animation. */
    play() { state.play(); drawControls(); startLoop(); },
    pause() { state.pause(); stopLoop(); drawControls(); },
    destroy() {
      if (dead) return;
      dead = true;
      stopLoop();
      for (const off of offs) off();
      if (lock && !lock.released) lock.release().catch(() => {});
      lock = null;
      render(el, '');
    },
  };
}
